import { styled } from '@mui/system';
import { useAtomValue } from 'jotai';
import { showLeadershipCardOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { FinancingHistoryTable } from '../../CompanyProfiles/Summary/components/FinancingHistory/FinancingHistoryTable';
import { NewsSection } from '../../CompanyProfiles/Summary/components/NewsSection';
import { selectedCompanyIdProfileAtom } from '../../CompanyProfiles/state/UIStateJ';
import { SummarySection } from './SummarySection';
import { DigestPanel } from './DigestPanel';
import { RecentActivityPanel } from './RecentActivity/RecentActivityPanel';
import { SummaryStrongestConnections } from './SummaryStrongestConnections';
import { LeadershipPanel } from './LeadershipPanel';
import {
  CompanyProfilesContainerBreakpoint,
  summaryCardCss,
  summaryPageBackground,
} from './summary-layout.styles';

const MainWrapper = styled('div')`
  background: ${summaryPageBackground};
  container-name: company-profiles-container;
  container-type: inline-size;
  display: grid;
  height: 100%;
  width: 100%;
`;

const SummaryLayout = styled('div', { shouldForwardProp: (prop) => prop !== 'showLeadership' })<{
  showLeadership: boolean;
}>`
  align-content: start;
  // Items stretch (the grid default) so cards sharing a row match heights. Safe now that the card
  // chrome sits on the collapsible body rather than the slot: a collapsed card's slot still
  // stretches, but there is no bordered box to leave behind — only its heading shows.
  display: grid;
  gap: 1rem;
  padding: 1rem;
  width: 100%;

  @container company-profiles-container (width < ${CompanyProfilesContainerBreakpoint}) {
    grid-template-columns: 1fr 0fr;
    grid-template-areas:
      'digest'
      'digest'
      'digest'
      ${({ showLeadership }) => (showLeadership ? "'leader'" : '')}
      'connections'
      'connections'
      'connections'
      'activity'
      'activity'
      'news'
      'news'
      'fin-hist'
      'fin-hist';
  }

  @container company-profiles-container (width >= ${CompanyProfilesContainerBreakpoint}) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      ${({ showLeadership }) =>
        showLeadership
          ? "'digest leader' 'digest connections' 'digest connections'"
          : "'digest connections' 'digest connections' 'digest connections'"}
      'news activity'
      'news activity'
      'fin-hist fin-hist'
      'fin-hist fin-hist';
  }
`;

export function CompanySummaryDiscoveryV2() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const showLeadership = useAtomValue(showLeadershipCardOrgState);
  return (
    <MainWrapper>
      <SummaryLayout showLeadership={showLeadership}>
        <div css={summaryCardCss} style={{ gridArea: 'digest' }}>
          <DigestPanel companyId={companyId} />
        </div>
        {showLeadership && (
          <div css={summaryCardCss} style={{ gridArea: 'leader' }}>
            <LeadershipPanel companyId={companyId} />
          </div>
        )}
        <div css={summaryCardCss} style={{ gridArea: 'connections' }}>
          <SummaryStrongestConnections />
        </div>
        <div css={summaryCardCss} style={{ gridArea: 'news' }}>
          <SummarySection title={'News'}>
            <NewsSection />
          </SummarySection>
        </div>
        <div css={summaryCardCss} style={{ gridArea: 'activity' }}>
          <RecentActivityPanel />
        </div>
        <div css={summaryCardCss} style={{ gridArea: 'fin-hist' }}>
          <SummarySection
            title={'Financing History'}
            showMore={{ url: '../financials', title: 'Go to Financials' }}
          >
            <FinancingHistoryTable />
          </SummarySection>
        </div>
      </SummaryLayout>
    </MainWrapper>
  );
}
